import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import client from '../../../lib/feathers';
import toast from 'react-hot-toast';
import {
  faTrash,
  faPlus,
  faPencil,
  faCheck,
  faXmark,
  faFileLines,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const TYPE_OPTIONS = ['BOTTLE', 'LABEL', 'DOCUMENT'];
const POSITION_OPTIONS = ['FRONT', 'BACK', 'SIDE', 'WRAP', 'NONE'];
const SUBTYPE_OPTIONS = ['SELL_SHEET', 'TECH_SHEET', 'PRESS_KIT', 'NONE'];

const typeLabel = (val) => val.charAt(0) + val.slice(1).toLowerCase();
const subtypeLabel = (val) => val === 'NONE' ? 'None' : val.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

const LibationAttachments = ({
  targetType,
  targetId,
  libationId,          // enables moving an attachment between this wine and its vintages
  readOnly = false,
  badgeLabel,
  title,
  description,
}) => {
  const user = useSelector((state) => state.auth.user);
  const [attachments, setAttachments] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // New attachment form state
  const [newType, setNewType] = useState('LABEL');
  const [newPosition, setNewPosition] = useState('FRONT');
  const [newSubtype, setNewSubtype] = useState('NONE');

  // Edit form state
  const [editType, setEditType] = useState('');
  const [editPosition, setEditPosition] = useState('');
  const [editSubtype, setEditSubtype] = useState('');
  const [editSortOrder, setEditSortOrder] = useState(0);

  // Preview modal state
  const [previewUrl, setPreviewUrl] = useState(null);

  // Where this attachment can be moved (a wine ↔ its own vintages). Built from
  // `libationId`: a vintage's media can move up to the wine; the wine's media can
  // move down to a specific vintage.
  const [moveTargets, setMoveTargets] = useState([]);

  const servicePath = `${user.role}/libation-attachment`;

  const sectionTitle = title || 'Labels & Documents';
  const sectionDescription = description || (
    targetType === 'libation'
      ? 'Generic bottle photos, label images, and trade documents that apply to every vintage of this wine.'
      : 'Bottle photos, label images, and trade documents specific to this vintage.'
  );

  const loadAttachments = async () => {
    try {
      const service = client.service(servicePath);
      const result = await service.find({
        query: { targetType, targetId, $sort: { sortOrder: 1 }, $limit: 50 }
      });
      setAttachments(result.data || result);
    } catch (err) {
      console.error('Error loading libation attachments:', err);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (targetType && targetId) {
      loadAttachments();
    }
  }, [targetType, targetId]);

  // Compute available move destinations.
  useEffect(() => {
    let cancelled = false;
    const computeTargets = async () => {
      if (readOnly || !libationId) { setMoveTargets([]); return; }
      if (targetType === 'libationVintage') {
        setMoveTargets([{
          key: `libation:${libationId}`, targetType: 'libation', targetId: libationId,
          label: 'Wine (all vintages)',
        }]);
        return;
      }
      if (targetType === 'libation') {
        try {
          const res = await client.service(`${user.role}/libation-vintage`).find({
            query: { libationId, $limit: 100 },
          });
          const lvs = res.data || res || [];
          const targets = lvs.map((lv) => {
            const year = lv.vintage && lv.vintage.year;
            return {
              key: `libationVintage:${lv.id}`, targetType: 'libationVintage', targetId: lv.id,
              year: year || 0,
              label: `Vintage ${year === 9999 ? 'NV' : (year || '')}`.trim(),
            };
          }).sort((a, b) => b.year - a.year);
          if (!cancelled) setMoveTargets(targets);
        } catch (err) {
          if (!cancelled) setMoveTargets([]);
        }
      }
    };
    computeTargets();
    return () => { cancelled = true; };
  }, [targetType, libationId, readOnly, user.role]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setIsUploading(true);

    try {
      for (const file of acceptedFiles) {
        const id = uuidv4();
        // Include file extension in the S3 key so CloudFront Lambda can detect file type
        const ext = file.name.split('.').pop()?.toLowerCase() || (file.type === 'application/pdf' ? 'pdf' : 'jpg');
        const uploadService = client.service(`${user.role}/attachment-upload`);
        const urls = await uploadService.create({
          'file-name': `vintage-attachments/${id}.${ext}`,
          'file-type': file.type,
        });

        await axios.put(urls.signedRequest, file, {
          headers: { 'Content-Type': file.type },
        });

        // Create the attachment record
        const attachmentService = client.service(`${user.role}/attachment`);
        await attachmentService.create({
          id,
          path: urls.path,
          type: file.type.startsWith('image/') ? 'photo' : 'document',
          mime: file.type,
        });

        // Create the polymorphic junction record
        const service = client.service(servicePath);
        await service.create({
          targetType,
          targetId,
          attachmentId: id,
          type: newType,
          position: newType === 'DOCUMENT' ? 'NONE' : newPosition,
          subtype: newType === 'DOCUMENT' ? newSubtype : 'NONE',
          sortOrder: attachments.length,
        });
      }

      toast.success(acceptedFiles.length === 1 ? 'Attachment added' : `${acceptedFiles.length} attachments added`);
      setIsAdding(false);
      resetNewForm();
      await loadAttachments();
    } catch (err) {
      toast.error(err.message || 'Failed to upload');
    }
    setIsUploading(false);
  }, [newType, newPosition, newSubtype, attachments.length, targetType, targetId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: newType === 'DOCUMENT'
      ? { 'application/pdf': ['.pdf'] }
      : { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    disabled: isUploading || readOnly,
  });

  const resetNewForm = () => {
    setNewType('LABEL');
    setNewPosition('FRONT');
    setNewSubtype('NONE');
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditType(item.type);
    setEditPosition(item.position);
    setEditSubtype(item.subtype);
    setEditSortOrder(item.sortOrder);
  };

  const handleSaveEdit = async (id) => {
    try {
      const service = client.service(servicePath);
      await service.patch(id, {
        type: editType,
        position: editType === 'DOCUMENT' ? 'NONE' : editPosition,
        subtype: editType === 'DOCUMENT' ? editSubtype : 'NONE',
        sortOrder: editSortOrder,
      });
      toast.success('Attachment updated');
      setEditingId(null);
      await loadAttachments();
    } catch (err) {
      toast.error(err.message || 'Failed to update');
    }
  };

  const handleMove = async (item, target) => {
    if (!target) return;
    if (!window.confirm(`Move this attachment to "${target.label}"?`)) return;
    try {
      const service = client.service(servicePath);
      await service.patch(item.id, { targetType: target.targetType, targetId: target.targetId });
      toast.success(`Moved to ${target.label}`);
      await loadAttachments(); // it now lives elsewhere → drops out of this list
    } catch (err) {
      toast.error(err.message || 'Failed to move');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      const service = client.service(servicePath);
      await service.remove(item.id);
      toast.success('Attachment removed');
      await loadAttachments();
    } catch (err) {
      toast.error(err.message || 'Failed to remove');
    }
  };

  const isImage = (item) => {
    return item.attachment && item.attachment.mime && item.attachment.mime.startsWith('image/');
  };

  if (isLoading) return null;

  // In read-only mode with no attachments, render nothing — no point showing
  // an empty section just to say "nothing here".
  if (readOnly && attachments.length === 0) return null;

  return (
    <div className="bg-white shadow px-4 py-5 sm:rounded-lg my-5">
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="md:col-span-1">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{sectionTitle}</h3>
          <p className="mt-1 text-sm text-gray-500">{sectionDescription}</p>
        </div>

        <div className="mt-5 md:mt-0 md:col-span-2">
          {/* Existing attachments */}
          <div className="space-y-2">
            {attachments.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 border rounded-md bg-gray-50">
                {!readOnly && editingId === item.id ? (
                  <>
                    <div className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded overflow-hidden flex items-center justify-center">
                      {isImage(item) ? (
                        <img
                          src={`${process.env.REACT_APP_ATTACHMENT_URL}/${item.attachment.path}?w=64&h=64`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faFileLines} className="text-gray-400 text-2xl" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex gap-2">
                        <select value={editType} onChange={(e) => setEditType(e.target.value)}
                          className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                          {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                        </select>
                        {editType !== 'DOCUMENT' && (
                          <select value={editPosition} onChange={(e) => setEditPosition(e.target.value)}
                            className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                            {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{typeLabel(p)}</option>)}
                          </select>
                        )}
                        {editType === 'DOCUMENT' && (
                          <select value={editSubtype} onChange={(e) => setEditSubtype(e.target.value)}
                            className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                            {SUBTYPE_OPTIONS.map((s) => <option key={s} value={s}>{subtypeLabel(s)}</option>)}
                          </select>
                        )}
                        <input
                          type="number"
                          min="0"
                          value={editSortOrder}
                          onChange={(e) => setEditSortOrder(parseInt(e.target.value) || 0)}
                          placeholder="Order"
                          className="block w-20 shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                        />
                      </div>
                    </div>
                    <button onClick={() => handleSaveEdit(item.id)} className="text-green-600 hover:text-green-800 p-1">
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600 p-1">
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className="flex-shrink-0 w-16 h-16 bg-gray-200 rounded overflow-hidden flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary"
                      onClick={() => {
                        const url = `${process.env.REACT_APP_ATTACHMENT_URL}/${item.attachment.path}`;
                        if (isImage(item)) {
                          setPreviewUrl(`${url}?f=inside`);
                        } else {
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      {isImage(item) ? (
                        <img
                          src={`${process.env.REACT_APP_ATTACHMENT_URL}/${item.attachment.path}?w=64&h=64`}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FontAwesomeIcon icon={faFileLines} className="text-gray-400 text-2xl" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-800">
                          {typeLabel(item.type)}
                        </span>
                        {item.type !== 'DOCUMENT' && item.position !== 'NONE' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {typeLabel(item.position)}
                          </span>
                        )}
                        {item.type === 'DOCUMENT' && item.subtype !== 'NONE' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {subtypeLabel(item.subtype)}
                          </span>
                        )}
                        {badgeLabel && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            ✨ {badgeLabel}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {item.attachment?.caption || (item.attachment?.mime || '')}
                        {item.sortOrder > 0 && <span className="ml-2">Order: {item.sortOrder}</span>}
                      </div>
                    </div>
                    {!readOnly && (
                      <>
                        {moveTargets.length > 0 && (
                          <select
                            value=""
                            onChange={(e) => handleMove(item, moveTargets.find((m) => m.key === e.target.value))}
                            className="text-xs border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500"
                            title="Move this attachment between the wine and its vintages"
                          >
                            <option value="">Move…</option>
                            {moveTargets.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                          </select>
                        )}
                        <button onClick={() => handleStartEdit(item)} className="text-gray-400 hover:text-gray-600 p-1">
                          <FontAwesomeIcon icon={faPencil} size="sm" />
                        </button>
                        <button onClick={() => handleDelete(item)} className="text-gray-400 hover:text-red-600 p-1">
                          <FontAwesomeIcon icon={faTrash} size="sm" />
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
            ))}

            {attachments.length === 0 && !isAdding && !readOnly && (
              <p className="text-sm text-gray-400 italic">No labels or documents added yet.</p>
            )}
          </div>

          {/* Add new attachment */}
          {!readOnly && (isAdding ? (
            <div className="mt-3 p-3 border-2 border-dashed border-gray-300 rounded-md">
              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <select value={newType} onChange={(e) => setNewType(e.target.value)}
                    className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                    {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
                  </select>
                  {newType !== 'DOCUMENT' && (
                    <select value={newPosition} onChange={(e) => setNewPosition(e.target.value)}
                      className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                      {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{typeLabel(p)}</option>)}
                    </select>
                  )}
                  {newType === 'DOCUMENT' && (
                    <select value={newSubtype} onChange={(e) => setNewSubtype(e.target.value)}
                      className="block shadow-sm sm:text-sm border-gray-300 rounded-md focus:ring-gray-500 focus:border-gray-500">
                      {SUBTYPE_OPTIONS.map((s) => <option key={s} value={s}>{subtypeLabel(s)}</option>)}
                    </select>
                  )}
                </div>

                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer
                    ${isDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
                    ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} />
                  {isUploading ? (
                    <p className="text-sm text-gray-500">Uploading...</p>
                  ) : isDragActive ? (
                    <p className="text-sm text-gray-600">Drop files here</p>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {newType === 'DOCUMENT'
                        ? 'Click or drag PDF files here'
                        : 'Click or drag images here (JPG, PNG, WebP)'}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => { setIsAdding(false); resetNewForm(); }}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsAdding(true)}
              className="mt-3 inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80"
            >
              <FontAwesomeIcon icon={faPlus} size="sm" />
              Add Attachment
            </button>
          ))}
        </div>
      </div>

      {/* Image preview modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/70 overflow-auto"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="sticky top-0 z-10 flex justify-end items-center gap-3 p-4">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-white hover:text-gray-300 text-sm underline"
            >
              Open in new tab
            </a>
            <button
              onClick={() => setPreviewUrl(null)}
              className="text-white hover:text-gray-300"
            >
              <FontAwesomeIcon icon={faXmark} size="2x" />
            </button>
          </div>
          <div className="flex justify-center px-4 pb-4">
            <img
              src={previewUrl}
              alt="Preview"
              className="max-w-[90vw] rounded shadow-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LibationAttachments;
